import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group"
import { Label } from "../components/ui/label"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"

export function RadioRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults: theme-variant/size surfaced on data-* attrs only.
  // Legacy pre-v2 radio blocks overload `element.variant` as the radio's
  // value carrier (see `const value = element.variant || element.text || ""`
  // below); that semantic is preserved and is separate from the theme-v2
  // data-variant contract.
  const atomDefaults = useAtomDefaults("radio")
  const resolvedVariant = atomDefaults.defaultVariant ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : undefined
  const name = element.name || element.id
  const value = element.variant || element.text || ""

  // Bind resolves the *group* value; this radio is checked when group === own value
  const [groupVal, setGroupVal] = useBoundValue<string>(
    element.bind,
    resolveSymbol,
    "",
    String,
  )

  const handleChange = (newValue: string) => {
    setGroupVal(newValue)
    dispatchEvent(element.id, "change", { value: newValue })
  }

  // Single radio item wrapped in a minimal RadioGroup for proper shadcn styling
  return (
    <RadioGroup
      value={element.bind !== undefined ? groupVal : undefined}
      onValueChange={handleChange}
      className={cn("flex items-center gap-2", stripPresetConflicts(element.className))}
      data-testid={`radio-${element.id}`}
      data-action="input"
      data-field-type="radio"
      data-atom-type="radio"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    >
      <RadioGroupItem id={element.id} value={value} />
      {text && <Label htmlFor={element.id}>{text}</Label>}
    </RadioGroup>
  )
}
